import React, {useContext, useEffect, useState} from "react";
import {Accordion, AccordionContext, Button, Modal, Spinner, useAccordionButton} from "react-bootstrap";
import ConfirmDialog from "./confirm-dialog";
import {validateFormula} from "../../api-service/storage-provider";
import Checkbox from "@mui/material/Checkbox";
import {FormControlLabel} from "@mui/material";

interface FormulaModalProps {
    show: boolean;
    onClose: () => void;
    value: FormulaValue;
    onChange: (val: FormulaValue) => void;
    onSave: (val: FormulaValue) => void;
    initialValue?: FormulaValue;
    currentField?: string | null;
}

type FormulaValue = {
    index: any;
    convertRule: string;
    notSend: boolean;
};

const rules = [
    {
        title: "SUBSTRING(1,3)",
        description: "文字列の先頭3文字を切り出す",
        example: '例:"<b>abc</b>defg" → "<b>abc<b>"',
    },
    {
        title: 'CONCAT("20", value)',
        description: '文字列の前に "20" を付ける',
        example: '例:valueが"<b>25</b>-09-22" → "<b>2025</b>-09-22"',
        note: "※ valueは変更してないで利用してください",
    },
    {
        title: "CONCAT(SUBSTRING(1,4), SUBSTRING(12,2))",
        description: "文字列の1文字目から4文字と先頭12文字目から2文字を連結する",
        example: '例:"<b>abcd</b>efghij1<b>23</b>4567890" → "<b>abcd23</b>"',
    },
    {
        title: 'CASE(value, "A", "1", "B", "2", "5")',
        description: "valueの値にて条件分岐して値を変換する",
        example: '例:valueが"<b>B</b>" → "<b>2</b>"',
        note: "※ valueは変更してないで利用してください",
    },
    {
        title: 'DATEFORMAT("YYYY-MM-DD HH:mm:ss")',
        description: "日時を指定フォーマットに変換する",
        example: '例:"2023-10-26<b>T09:06:09Z</b>" → "2023-10-26 <b>18:06:09</b>"',
    },
    {
        title: 'IF(value=="Login", "1", "2")',
        description: '値が "Login" の場合は "<b>1</b>"、それ以外は "<b>2</b>" に変換する',
        example: '例:valueが"LOGOUT" → "<b>2</b>"',
        note: "※ valueは変更してないで利用してください",
    },
];

function RuleList() {
    return (
        <div style={{backgroundColor: '#F3F3F3', borderRadius: '10px'}}>
            <ol style={{fontSize: '13px', paddingTop: '12px', marginBottom: 0}}>
                {rules.map((rule, idx) => (
                    <li key={idx} className="pb-3">
                        <div>{rule.title}</div>
                        <div dangerouslySetInnerHTML={{__html: rule.description}}></div>
                        <div dangerouslySetInnerHTML={{__html: rule.example}}></div>
                        {rule.note && (
                            <div>{rule.note}</div>
                        )}
                    </li>
                ))}
            </ol>
        </div>
    );
}

const RulesAccordionArrow = (props) => {
    return <div
        style={{
            width: '16px',
            height: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: "#747474",
            marginRight: '4px',
            transition: "transform 180ms ease",
            ...props.style,
        }}
    >
        <svg width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" clipRule="evenodd"
                  d="M1.50793 0.853849L7.87716 7.16154C8.06178 7.34616 8.06178 7.65385 7.87716 7.83846L1.50793 14.1462C1.32332 14.3308 1.01563 14.3308 0.83101 14.1462L0.154087 13.4692C-0.0305289 13.2846 -0.0305289 12.9769 0.154087 12.7923L5.16947 7.83846C5.35409 7.65385 5.35409 7.34616 5.16947 7.16154L0.184856 2.2077C0.00024038 2.02308 0.00024038 1.71539 0.184856 1.53077L0.861779 0.853849C1.04639 0.700003 1.32332 0.700003 1.50793 0.853849Z"
                  fill="#747474"/>
        </svg>
    </div>
}

function CustomHeader({eventKey, children}) {
    const {activeEventKey} = useContext(AccordionContext);
    const toggle = useAccordionButton(
        eventKey,
    );
    const isOpen = activeEventKey === eventKey;

    return (
        <div
            onClick={toggle}
            aria-expanded={!!isOpen}
            style={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                background: "transparent",
                border: "none",
                padding: 0,
                textAlign: "left",
                cursor: "pointer",
            }}
        >
            <RulesAccordionArrow style={{transform: isOpen ? "rotate(90deg)" : "rotate(0deg)"}}/>
            <div style={{fontSize: '14px', color: '#0176D3'}}>
                {children}
            </div>
        </div>
    );
}

function RulesAccordion() {
    return (
        <Accordion>
            <Accordion.Item eventKey="0" style={{border: 'none'}}>
                <CustomHeader eventKey="0">変数のルール</CustomHeader>
                <Accordion.Body style={{padding: '0 0 0 0', marginTop: '8px'}}>
                    <RuleList/>
                </Accordion.Body>
            </Accordion.Item>
        </Accordion>
    );
}

const FormulaModal: React.FC<FormulaModalProps> = ({
                                                       show,
                                                       onClose,
                                                       value,
                                                       onChange,
                                                       onSave,
                                                       initialValue,
                                                       currentField,
                                                   }) => {
    const [confirmType, setConfirmType] = useState<"save" | null>(
        null,
    );
    const [currentIndex, setCurrentIndex] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isValidated, setIsValidated] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isShowCheckbox, setIsShowCheckbox] = useState<boolean>(false);
    const [isNotSend, setIsNotSend] = useState<boolean>(false);
    const fieldShowCheckbox = ["EmpId__c", "Email__c", "Note__c", "EmpCode__c"];
    const [isValidationError, setIsValidationError] = useState<boolean>(false);
    const [validationResult, setValidationResult] = useState<{
        before: string;
        after: string;
        message: string;
    }>({
        before: "",
        after: "",
        message: "",
    });

    const handleConfirm = () => {
        if (confirmType === "save") {
            const newVal = {...value};
            setCurrentIndex(value.index);
            onChange(newVal);
            onSave(newVal);
        }
    };

    useEffect(() => {
        handleRenderModal();
        setError(null);
        setCurrentIndex(value.index);
        setIsNotSend(value.notSend);
        setIsValidationError(false);
        setIsValidated(false);
        handleClearIndex();
    }, [initialValue]);

    useEffect(() => {
        handleRenderModal();
        setValidationResult({
            before: "",
            after: "",
            message: "",
        });
        setIsValidationError(false);
        setIsValidated(false);
    }, []);

    useEffect(() => {
        if (value.convertRule === "" || value.convertRule.trim() === "" || !value) {
            setIsValidationError(false);
            setIsValidated(true);
            setValidationResult({
                before: "",
                after: "",
                message: "",
            });
        }
    }, [value, value.convertRule]);

    const handleValidateFormula = async () => {
        setIsLoading(true);
        try {
            const res = await validateFormula(value.convertRule);
            setValidationResult({
                before: res?.example?.before ?? "",
                after: res?.example?.after ?? "",
                message: res?.message ?? "",
            });
            setIsValidationError(false);
        } catch (error) {
            setIsValidationError(true);
        } finally {
            setIsValidated(true);
            setIsLoading(false);
        }
    };

    const handleRenderModal = async () => {
        if (currentField && fieldShowCheckbox.includes(currentField)) {
            setIsShowCheckbox(true);
        } else {
            setIsShowCheckbox(false);
        }
    };

    const handleToggleSendingType = () => {
        setIsNotSend((prev) => {
            const newValue = !prev;
            onChange({...value, notSend: newValue});
            return newValue;
        });
    };

    const handleClearIndex = async () => {
        setIsValidated(true);
        setIsValidationError(false);
        setError(null);
        setValidationResult({
            before: "",
            after: "",
            message: "",
        });
    };

    useEffect(() => {
        if (isNotSend) {
            handleClearIndex();
        } else {
            if (value.convertRule.trim() !== "") {
                setIsValidated(false);
            }
        }
    }, [isNotSend]);

    return (
        <>
            {isLoading && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100vw",
                        height: "100vh",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "rgba(255, 255, 255, 0.6)",
                        zIndex: 1500,
                    }}
                >
                    <Spinner variant="secondary"/>
                </div>
            )}

            <Modal
                animation={true}
                size="lg"
                className="formula-modal"
                show={show}
                onHide={onClose}
                centered
                enforceFocus={false}
                restoreFocus={false}
            >
                <Modal.Header className="formula-modal-header" closeButton>
                    <Modal.Title className="sm-title">取得列・変換数式の設定</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <ConfirmDialog
                        show={confirmType !== null}
                        onClose={() => setConfirmType(null)}
                        onConfirm={handleConfirm}
                        message={(confirmType === "save"
                                ? "取得列を変更すると、変換数式が削除されます。\n変更しますか？"
                                : "入力されている変換数式を削除し、未入力に戻します。\n削除してよろしいですか？"
                        )
                            .split("\n")
                            .map((line, idx) => (
                                <span key={idx}>
                  {line}
                                    <br/>
                </span>
                            ))}
                        type="save"
                    />
                    <div className="formula-modal-input-container d-flex gap-3 align-items-start">
                        <label>
                            <span className="text-danger">*&nbsp;</span>
                            取得列
                        </label>
                        <div className="modal-index-checkbox">
                            <input
                                disabled={isNotSend}
                                type="number"
                                required
                                className={`form-control time-input ${
                                    error ? "is-invalid" : ""
                                }`}
                                value={isNotSend ? "" : String(value.index)}
                                onKeyDown={(e) => {
                                    if (["e", "E", "+", "-", "."].includes(e.key)) {
                                        e.preventDefault();
                                    }
                                }}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    let num = val === "" ? "" : Number(val.replace(/^0+/, ""));
                                    if (num === 0) {
                                        setError("0は入力できません");
                                    } else if (!val) {
                                        setError("空欄は登録できません");
                                    } else if (Number(num) > 500) {
                                        setError("取得列は500までしか登録できません");
                                    } else {
                                        setError(null);
                                    }
                                    onChange({...value, index: num});
                                    setIsValidated(currentIndex === num ? false : true);
                                }}
                            />
                            {error && <span className="formula-error-text">{error}</span>}
                            {isShowCheckbox && (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            onChange={handleToggleSendingType}
                                            icon={
                                                <span
                                                    style={{
                                                        width: 16,
                                                        height: 16,
                                                        border: "1px solid #DDDBDA",
                                                        borderRadius: "2px",
                                                        display: "inline-block",
                                                    }}
                                                />
                                            }
                                            checked={isNotSend}
                                            checkedIcon={
                                                <span
                                                    style={{
                                                        width: 16,
                                                        height: 16,
                                                        border: "1px solid #0070D2",
                                                        borderRadius: "2px",
                                                        display: "inline-block",
                                                        backgroundColor: "#fff",
                                                        position: "relative",
                                                    }}
                                                >
                          <span
                              style={{
                                  content: '""',
                                  position: "absolute",
                                  left: "5.2px",
                                  top: "1px",
                                  width: "4px",
                                  height: "10px",
                                  border: "solid #0070D2",
                                  borderWidth: "0 2px 2px 0",
                                  transform: "rotate(45deg)",
                              }}
                          />
                        </span>
                                            }
                                            sx={{padding: 0}}
                                        />
                                    }
                                    label="送信しない"
                                    sx={{
                                        marginLeft: "4px",
                                        "& .MuiFormControlLabel-label": {
                                            fontSize: "13px",
                                            marginLeft: "10px",
                                            color: "#3E3E3C",
                                        },
                                    }}
                                />
                            )}
                        </div>
                    </div>
                    {isShowCheckbox && (
                        <div className="formula-modal-input-container d-flex gap-3 align-items-start mt-2">
                            <label></label>
                            <div className="formula-guild-text">
                <span>
                  メール、社員ID、社員コードをすべて「送信しない」に設定することはできません
                </span>
                            </div>
                        </div>
                    )}
                    <div className="formula-modal-input-container d-flex gap-3 align-items-start mt-2">
                        <label>変換数式</label>
                        <textarea
                            maxLength={100000}
                            id="formula-modal-input"
                            disabled={isNotSend}
                            value={isNotSend ? "" : value.convertRule}
                            onChange={(e) => {
                                onChange({...value, convertRule: e.target.value});
                                if (e.target.value.trim() === "") {
                                    setIsValidated(true);
                                } else {
                                    setIsValidated(false);
                                }
                            }}
                            className="formula-modal-input"
                            style={{flex: 1, minHeight: "80px", padding: "8px"}}
                        />
                    </div>
                    <div className="formula-modal-input-container d-flex gap-3 align-items-start mt-2">
                        <label></label>
                        <div style={{width: '100%'}}>
                            <RulesAccordion/>
                        </div>
                    </div>
                    <div className="formula-modal-input-container d-flex gap-3 align-items-start mt-2">
                        <label></label>
                        <div className="formula-guild-text">
                            <span>数式を入力する場合はエラーチェックが必要です</span>
                        </div>
                    </div>
                    <div className="formula-modal-input-container d-flex gap-3 align-items-start">
                        <label></label>
                        <Button
                            variant="outline-primary formular-validation-btn"
                            onClick={handleValidateFormula}
                            disabled={value.convertRule.trim() === "" || isNotSend}
                            className={
                                value.convertRule.trim() === "" || isNotSend
                                    ? "button-disabled"
                                    : ""
                            }
                        >
                            エラーチェック
                        </Button>
                    </div>
                    {isValidated && (
                        <>
                            <div className="formula-modal-input-container d-flex gap-3 align-items-start">
                                <label></label>
                                <div className="formula-guild-text validate-res">
                                    {!isValidationError && validationResult.before && (
                                        <span>
                      結果 ： 入力値 ： {validationResult.before} &nbsp; &nbsp;
                                            テスト値 ： {validationResult.after}
                    </span>
                                    )}

                                    {isValidationError && isValidated && (
                                        <span>
                      結果 ： &nbsp; &nbsp;
                                            <span className="formula-error-format">
                        エラー ： #REF! エラー
                      </span>
                    </span>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>

                    <Button
                        size="sm"
                        variant="outline-primary bg-white modal-formula-cancel-only modal-formula-cancel"
                        onClick={onClose}
                    >
                        キャンセル
                    </Button>
                    <Button
                        size="sm"
                        variant="primary"
                        className={`modal-formula-save ms-2 ${
                            value.notSend === true
                                ? ""
                                : isValidationError || !isValidated
                                    ? "formula-save-disabled"
                                    : ""
                        }`}
                        disabled={
                            value.notSend === true ? false : isValidationError || !isValidated
                        }
                        onClick={() => {
                            if (error !== null) {
                                return;
                            }
                            setConfirmType(null);
                            if (!isNotSend && (!value.index || value.index === 0)) {
                                setError("Can not be empty");
                                return;
                            }
                            if (isNotSend) {
                                const newValue = {
                                    ...value,
                                    index: undefined,
                                    convertRule: "",
                                };
                                onChange(newValue);
                                onSave(newValue);
                                return;
                            }
                            onSave(value);
                        }}
                    >
                        保存
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default FormulaModal;
